"use client";

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { EmptyTable } from "./components/EmptyTable";

/** Alinhamento do título da coluna no cabeçalho (a célula mantém seu próprio estilo). */
type HeaderAlignment = "center" | "right";

export type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<
  TData,
  TValue
> & {
  className?: string;
  sticky?: "left-0" | "right-0";
  headerAlignment?: HeaderAlignment;
};

const HEADER_ALIGNMENT_CLASS: Record<HeaderAlignment, string> = {
  center: "text-center",
  right: "text-right",
};

interface DataTableProps<TData, TValue> {
  columns: ExtendedColumnDef<TData, TValue>[];
  data: TData[];
  hasHeader?: boolean;
  emptyMessage?: string | React.ReactNode;
  emptyClassname?: string;
  getRowClassName?: (row: TData) => string;
  isLoading?: boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  hasHeader = true,
  emptyMessage,
  emptyClassname,
  getRowClassName,
  isLoading = false,
}: DataTableProps<TData, TValue>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {},
  });

  return (
    <Table className="w-full max-w-full border overflow-x-auto rounded-xl">
      {hasHeader && (
        <TableHeader className="bg-[#F9FBFC]">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const headerAlignment: HeaderAlignment | undefined = (
                  header.column.columnDef as ExtendedColumnDef<TData, TValue>
                ).headerAlignment;
                return (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    className={
                      headerAlignment
                        ? cn(HEADER_ALIGNMENT_CLASS[headerAlignment])
                        : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
      )}

      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={columns.length}>
              <div className="flex items-center justify-center py-24">
                <Loader2 className="h-8 w-8 animate-spin text-[#F4C83E]" />
              </div>
            </TableCell>
          </TableRow>
        ) : data?.length > 0 ? (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && "selected"}
              className={`p-0 mx-0 ${getRowClassName ? getRowClassName(row.original) : ""}`}
            >
              {row.getVisibleCells().map((cell, i) => (
                <TableCell
                  key={cell.id}
                  className={` ?? ""} cell-${i} px-0 mx-0`}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={columns.length}>
              <EmptyTable text={emptyMessage} className={emptyClassname} />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
